import React from 'react';
import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { EsignatureTokenStatusValue } from '~/features/ticket/types/esignature';
import { api } from '~/redux/api/createApi';

type Props = {
  ticketId?: string;
};

export const TokenStatusMessage = ({ ticketId }: Props) => {
  const intl = useIntl();

  const { data: tokenStatus, isLoading: isLoadingTokenStatus } =
    api.endpoints.getTokenStatus.useQuery({ ticketId });

  const { data: authenticateData, isLoading: isLoadingAuthenticate } =
    api.endpoints.getAuthenticate.useQuery(
      { return_to: window.location.href },
      {
        skip:
          !tokenStatus ||
          tokenStatus.accountToken !== EsignatureTokenStatusValue.Dead,
      },
    );

  if (isLoadingTokenStatus || !tokenStatus) {
    return null;
  }

  const { integrationType, accountToken, envelopeToken } = tokenStatus;
  const hasAccountTokenError = accountToken === EsignatureTokenStatusValue.Dead;
  const hasEnvelopeTokenError =
    envelopeToken === EsignatureTokenStatusValue.Dead;

  if (!hasAccountTokenError && !hasEnvelopeTokenError) {
    return null;
  }

  const isPersonalIntegration = integrationType === 'personal';

  const handleReAuthenticate = () => {
    if (authenticateData?.authenticateUrl) {
      window.location.href = authenticateData.authenticateUrl;
    }
  };

  return (
    <Layout direction="column" mt={2} mb={6} spacing={2}>
      {ticketId && hasEnvelopeTokenError && (
        <StatusMessage
          status="danger"
          title={intl.formatMessage({
            id: 'TokenStatusMessage.envelopeIntegrationErrorTitle',
            defaultMessage: 'Action Needed: E-Signature Status Not Syncing',
            description:
              'Title of error status message if problem with the e-signature envelopes status',
          })}
          message={intl.formatMessage({
            id: 'TokenStatusMessage.envelopeIntegrationError',
            defaultMessage: `This ticket isn't getting real-time updates from the e-signature platform. The Signing Coordinator must take one of the following actions to move the ticket forward:<ul><li><strong>Option A - Download and Upload:</strong>  If the agreement's already signed, just download it from your e-sign account. Then, upload it to Evisort to push this ticket to the next stage.</li><li><strong>Option B - Void and Re-initiate:</strong> Alternatively, you can void/cancel the agreement in your e-sign account. After that, simply re-initiate the signature process directly from Evisort. This will send a brand-new signature request to the signers</li></ul>`,
            description:
              'Error message for when a ticket is not being synced properly with the e-signature platform anymore',
          })}
        />
      )}

      {hasAccountTokenError && (
        <StatusMessage
          status="danger"
          message={
            isPersonalIntegration
              ? intl.formatMessage({
                  id: 'TokenStatusMessage.personalIntegrationError',
                  defaultMessage:
                    'Your e-signature connection with Evisort needs attention. Click the "Re-authenticate" button to grant the necessary permissions to avoid workflow disruptions',
                  description:
                    'Error message for personal e-signature integration issues',
                })
              : intl.formatMessage({
                  id: 'TokenStatusMessage.companyIntegrationError',
                  defaultMessage:
                    'Your e-signature connection with Evisort needs attention. Please contact your admin for further assistance.',
                  description:
                    'Error message for company e-signature integration issues',
                })
          }
          action={
            isPersonalIntegration
              ? {
                  text: intl.formatMessage({
                    id: 'TokenStatusMessage.reAuthenticateButton',
                    defaultMessage: 'Re-authenticate',
                    description:
                      'Button text for re-authenticating e-signature',
                  }),
                  onClick: handleReAuthenticate,
                  disabled:
                    isLoadingAuthenticate || !authenticateData?.authenticateUrl,
                }
              : undefined
          }
        />
      )}
    </Layout>
  );
};
